import { useState, useEffect, useRef } from "react";
import logoLmd from "@/assets/Logo_AiLum_Solutions.webp";
import logoLmd300 from "@/assets/Logo_AiLum_Solutions-300w.webp";
import { motion, type Easing } from "framer-motion";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useLanguage } from "@/contexts/LanguageContext";
import { z } from "zod";

const easeOut: Easing = [0.16, 1, 0.3, 1];

const contactSchema = z.object({
  name: z.string().trim().min(1, "Required").max(100),
  email: z.string().trim().email("Invalid email").max(255),
  phone: z.string().trim().max(20).optional(),
  message: z.string().trim().min(1, "Required").max(2000),
});

export const ContactSection = ({ subtitleOverride, accentColor = '#00d4ff' }: { subtitleOverride?: string; accentColor?: string }) => {
  const fieldClass = "backdrop-blur-md border-white/10 placeholder:text-white/40 transition-colors duration-200 focus:bg-white focus:text-black focus:border-white/50 focus:[background:white!important]";
  const emptyFieldStyle = { background: `${accentColor}28`, WebkitBackdropFilter: 'blur(12px)', color: 'rgba(255,255,255,0.85)' } as React.CSSProperties;
  const filledFieldStyle = { background: '#ffffff', WebkitBackdropFilter: 'blur(12px)', color: '#000000' } as React.CSSProperties;
  const getFieldStyle = (value: string) => value ? filledFieldStyle : emptyFieldStyle;

  const { t } = useLanguage();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [statusMessage, setStatusMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const statusTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });

  useEffect(() => {
    return () => { if (statusTimerRef.current) clearTimeout(statusTimerRef.current); };
  }, []);

  const showStatus = (type: 'success' | 'error', text: string) => {
    if (statusTimerRef.current) clearTimeout(statusTimerRef.current);
    setStatusMessage({ type, text });
    statusTimerRef.current = setTimeout(() => setStatusMessage(null), 10000);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatusMessage(null);

    const result = contactSchema.safeParse(formData);
    if (!result.success) {
      showStatus('error', result.error.errors[0]?.message || "Please check your input");
      return;
    }

    setIsSubmitting(true);
    try {
      const res = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          access_key: "c4294e0a-d14c-4031-a7fc-97036e5bb9a2",
          name: result.data.name,
          email: result.data.email,
          phone: result.data.phone ?? "",
          message: result.data.message,
          subject: "Nová poptávka z webu ailum.cz",
        }),
      });

      if (!res.ok) throw new Error("Network error");

      showStatus('success', t.contact.success);
      setFormData({ name: "", email: "", phone: "", message: "" });
    } catch {
      showStatus('error', t.contact.error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setStatusMessage(null);
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  return (
    <section id="kontakt" className="relative bg-transparent py-24 lg:py-32 scroll-mt-16 lg:scroll-mt-20" style={{ zIndex: 10 }}>
      <div className="max-w-7xl mx-auto px-6">
        {/* Header - full width */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, ease: easeOut }}
          className="mb-12 lg:mb-16"
        >
          <h2 
            className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6"
            style={{
              color: '#ffffff',
              textShadow: '0 2px 12px rgba(0, 0, 0, 0.5)'
            }}
          >
            {t.contact.title}
          </h2>
          <p 
            className="text-lg lg:text-xl"
            style={{
              color: 'rgba(255,255,255,0.85)',
              fontWeight: 500,
              textShadow: '0 2px 10px rgba(0, 0, 0, 0.6)',
              lineHeight: '1.8'
            }}
          >
            {subtitleOverride || t.contact.subtitle}
          </p>
        </motion.div>

        {/* Form (left) + Contact Details (right) */}
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24">
          {/* Form */}
          <motion.form
            onSubmit={handleSubmit}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, ease: easeOut, delay: 0.1 }}
            className="space-y-5"
          >
            <div className="grid sm:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label 
                  htmlFor="name"
                  style={{
                    color: 'rgba(255,255,255,0.85)',
                    fontWeight: 500,
                    textShadow: '0 1px 6px rgba(0, 0, 0, 0.5)'
                  }}
                >
                  {t.contact.form.name}
                </Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={`${fieldClass} h-12`}
                  style={getFieldStyle(formData.name)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label 
                  htmlFor="email"
                  style={{
                    color: 'rgba(255,255,255,0.85)',
                    fontWeight: 500,
                    textShadow: '0 1px 6px rgba(0, 0, 0, 0.5)'
                  }}
                >
                  {t.contact.form.email}
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`${fieldClass} h-12`}
                  style={getFieldStyle(formData.email)}
                  required
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label 
                htmlFor="phone"
                style={{
                  color: 'rgba(255,255,255,0.85)',
                  fontWeight: 500,
                  textShadow: '0 1px 6px rgba(0, 0, 0, 0.5)'
                }}
              >
                {t.contact.form.phone}
              </Label>
              <Input
                id="phone"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleChange}
                className={`${fieldClass} h-12`}
                style={getFieldStyle(formData.phone)}
              />
            </div>
            <div className="space-y-2">
              <Label 
                htmlFor="message"
                style={{
                  color: 'rgba(255,255,255,0.85)',
                  fontWeight: 500,
                  textShadow: '0 1px 6px rgba(0, 0, 0, 0.5)'
                }}
              >
                {t.contact.form.message}
              </Label>
              <Textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows={5}
                className={`${fieldClass} resize-none`}
                style={getFieldStyle(formData.message)}
                required
              />
            </div>
            <p className="text-xs leading-relaxed pt-0" style={{ color: 'rgba(255,255,255,0.4)' }}>
              {t.contact.form.gdpr}{' '}
              <a href="/zasady-ochrany-osobnich-udaju" className="underline hover:text-cyan-400 transition-colors">
                {t.contact.form.gdprLink}
              </a>.
            </p>
            <button
              type="submit"
              disabled={isSubmitting}
              className="group inline-flex items-center gap-3 rounded-[0.3rem] text-white border border-white hover:[background:white!important] hover:text-black transition-all duration-300 disabled:opacity-50 disabled:pointer-events-none font-bold"
              style={{ padding: '14px 28px', fontSize: '1.05rem', background: `linear-gradient(135deg, #000000 50%, ${accentColor}40 100%)` }}
            >
              {isSubmitting ? t.contact.form.submitting : t.contact.form.submit}
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="transition-all duration-300 group-hover:translate-x-1"
              >
                <line x1="5" y1="12" x2="19" y2="12" />
                <polyline points="12 5 19 12 12 19" />
              </svg>
            </button>
            {statusMessage && (
              <p
                className="text-lg font-semibold mt-3"
                style={{ color: statusMessage.type === 'success' ? '#22d3ee' : '#f87171' }}
              >
                {statusMessage.text}
              </p>
            )}
          </motion.form>

          {/* Contact Details */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, ease: easeOut, delay: 0.2 }}
            className="space-y-4 lg:pt-2"
          >
            <img
              src={logoLmd300}
              srcSet={`${logoLmd300} 300w, ${logoLmd} 914w`}
              sizes="(max-width: 768px) 150px, 255px"
              alt="Ailum Solutions - logo"
              width={300}
              height={101}
              loading="lazy"
              className="w-[40%] h-auto opacity-80 mb-8"
            />

            <p 
              className="text-xl font-semibold"
              style={{ color: '#ffffff', textShadow: '0 2px 10px rgba(0, 0, 0, 0.5)' }}
            >
              {t.contact.details.name}
            </p>
            
            <div className="space-y-2.5">
              <a 
                href={`mailto:${t.contact.details.email}`}
                className="flex items-center gap-3 hover:opacity-80 transition-opacity"
                style={{ color: 'rgba(255,255,255,0.85)' }}
              >
                <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
                </svg>
                <span className="text-sm">{t.contact.details.email}</span>
              </a>

              <a 
                href={`tel:${t.contact.details.phone.replace(/\s/g, '')}`}
                className="flex items-center gap-3 hover:opacity-80 transition-opacity"
                style={{ color: 'rgba(255,255,255,0.85)' }}
              >
                <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" />
                </svg>
                <span className="text-sm">{t.contact.details.phone}</span>
              </a>

              <div 
                className="flex items-center gap-3"
                style={{ color: 'rgba(255,255,255,0.6)' }}
              >
                <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 9h3.75M15 12h3.75M15 15h3.75M4.5 19.5h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5h-15A2.25 2.25 0 002.25 6.75v10.5A2.25 2.25 0 004.5 19.5zm6-10.125a1.875 1.875 0 11-3.75 0 1.875 1.875 0 013.75 0zm1.294 6.336a6.721 6.721 0 01-3.17.789 6.721 6.721 0 01-3.168-.789 3.376 3.376 0 016.338 0z" />
                </svg>
                <span className="text-sm">{t.contact.details.ico}</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
